import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SubtitleCue, fetchAndParseSRT, getActiveSubtitle } from '../utils/srtParser';
import useScreenWakeLock from '../hooks/useScreenWakeLock';
import './UnifiedAudioPlayer.css';

type SubtitleMode = 'off' | 'en' | 'zh-tw' | 'both';

interface UnifiedAudioPlayerProps {
  audioUrl: string;
  subtitleUrlEn?: string;
  subtitleUrlNative?: string;
  /** Inline subtitle cues — used as fallback when SRT URLs are not available */
  fallbackCuesEn?: SubtitleCue[];
  fallbackCuesNative?: SubtitleCue[];
  /** Constrain playback to a time range (for episode filtering) */
  startTime?: number;
  endTime?: number;
}

/** Format seconds → mm:ss */
const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const UnifiedAudioPlayer: React.FC<UnifiedAudioPlayerProps> = ({
  audioUrl,
  subtitleUrlEn,
  subtitleUrlNative,
  fallbackCuesEn,
  fallbackCuesNative,
  startTime,
  endTime,
}) => {
  const { t } = useTranslation('course');
  const audioRef = useRef<HTMLAudioElement>(null);

  // Playback state
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // Reset error when source changes
  useEffect(() => {
    setAudioError(false);
  }, [audioUrl]);

  useScreenWakeLock(playing);

  // Subtitle state
  const [subtitleMode, setSubtitleMode] = useState<SubtitleMode>('both');
  const [englishCues, setEnglishCues] = useState<SubtitleCue[]>([]);
  const [chineseCues, setChineseCues] = useState<SubtitleCue[]>([]);
  const [currentEnglish, setCurrentEnglish] = useState('');
  const [currentChinese, setCurrentChinese] = useState('');
  const rafRef = useRef<number>();

  const hasEnglish = !!(subtitleUrlEn || (fallbackCuesEn && fallbackCuesEn.length > 0));
  const hasNative = !!(subtitleUrlNative || (fallbackCuesNative && fallbackCuesNative.length > 0));
  const hasSubtitles = hasEnglish || hasNative;

  // Effective time range (for episode filtering)
  const effStart = startTime ?? 0;
  const effEnd = endTime ?? duration;
  const effDuration = effEnd - effStart;

  // Load SRT files, or use fallback cues
  useEffect(() => {
    if (subtitleUrlEn) {
      fetchAndParseSRT(subtitleUrlEn).then(setEnglishCues);
    } else if (fallbackCuesEn && fallbackCuesEn.length > 0) {
      setEnglishCues(fallbackCuesEn);
    }
    if (subtitleUrlNative) {
      fetchAndParseSRT(subtitleUrlNative).then(setChineseCues);
    } else if (fallbackCuesNative && fallbackCuesNative.length > 0) {
      setChineseCues(fallbackCuesNative);
    }
  }, [subtitleUrlEn, subtitleUrlNative, fallbackCuesEn, fallbackCuesNative]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      if (startTime !== undefined && audio.currentTime < startTime) {
        audio.currentTime = startTime;
      }
    };
    const onTimeUpdate = () => {
      if (!dragging) setCurrentTime(audio.currentTime);
      // Clamp to episode range
      if (endTime !== undefined && audio.currentTime >= endTime) {
        audio.pause();
        audio.currentTime = endTime;
        setPlaying(false);
      }
      if (startTime !== undefined && audio.currentTime < startTime) {
        audio.currentTime = startTime;
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [dragging, startTime, endTime]);

  // Seek to episode start when startTime changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || startTime === undefined) return;
    if (audio.readyState >= 1) {
      audio.currentTime = startTime;
    }
  }, [startTime]);

  // Subtitle sync via rAF (20fps)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || subtitleMode === 'off') {
      setCurrentEnglish('');
      setCurrentChinese('');
      return;
    }

    let last = 0;
    const tick = (ts: number) => {
      if (ts - last >= 50) {
        last = ts;
        const ct = audio.currentTime;
        if ((subtitleMode === 'en' || subtitleMode === 'both') && englishCues.length) {
          setCurrentEnglish(getActiveSubtitle(englishCues, ct)?.text || '');
        } else {
          setCurrentEnglish('');
        }
        if ((subtitleMode === 'zh-tw' || subtitleMode === 'both') && chineseCues.length) {
          setCurrentChinese(getActiveSubtitle(chineseCues, ct)?.text || '');
        } else {
          setCurrentChinese('');
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [subtitleMode, englishCues, chineseCues]);

  // Play / Pause
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audioError) return;
    if (playing) {
      audio.pause();
    } else {
      // If at end, restart from episode start
      if (endTime !== undefined && audio.currentTime >= endTime - 0.5) {
        audio.currentTime = effStart;
      }
      // play() rejects on missing/unsupported source; the onError handler on
      // <audio> surfaces it — swallow here so React doesn't show its runtime
      // error overlay for what is really a content issue.
      void audio.play().catch(() => undefined);
    }
  }, [playing, endTime, effStart, audioError]);

  // Progress bar interaction
  const progressRef = useRef<HTMLDivElement>(null);

  const seekFromEvent = useCallback((e: React.MouseEvent | MouseEvent) => {
    const bar = progressRef.current;
    const audio = audioRef.current;
    if (!bar || !audio) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = effStart + ratio * effDuration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [effStart, effDuration]);

  const handleProgressMouseDown = useCallback((e: React.MouseEvent) => {
    setDragging(true);
    seekFromEvent(e);

    const onMove = (ev: MouseEvent) => seekFromEvent(ev);
    const onUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [seekFromEvent]);

  // Touch support for progress bar
  const seekFromTouch = useCallback((e: React.TouchEvent | TouchEvent) => {
    const bar = progressRef.current;
    const audio = audioRef.current;
    if (!bar || !audio || !e.touches.length) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.touches[0].clientX - rect.left) / rect.width));
    const newTime = effStart + ratio * effDuration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [effStart, effDuration]);

  const handleProgressTouchStart = useCallback((e: React.TouchEvent) => {
    setDragging(true);
    seekFromTouch(e);

    const onMove = (ev: TouchEvent) => { ev.preventDefault(); seekFromTouch(ev); };
    const onEnd = () => {
      setDragging(false);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }, [seekFromTouch]);

  // Keyboard: space to toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
        togglePlay();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [togglePlay]);

  // Progress ratio
  const progressRatio = effDuration > 0
    ? Math.max(0, Math.min(1, (currentTime - effStart) / effDuration))
    : 0;

  const displayCurrent = Math.max(0, currentTime - effStart);
  const displayDuration = effDuration > 0 ? effDuration : duration;

  // Determine current cue index and prev/next navigation
  const activeCues = englishCues.length > 0 ? englishCues : chineseCues;
  const currentCueIndex = activeCues.findIndex(
    (cue, i) => currentTime >= cue.startTime && (i === activeCues.length - 1 || currentTime < activeCues[i + 1].startTime)
  );
  const hasActiveCues = activeCues.length > 0;
  // Prev: disabled only when at cue 0 AND near its start (nothing earlier to go to)
  const hasPrev = hasActiveCues && (currentCueIndex > 0 || (currentCueIndex === 0 && currentTime > activeCues[0].startTime + 1.5));
  const hasNext = hasActiveCues && currentCueIndex < activeCues.length - 1;

  const goToPrev = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !hasActiveCues) return;
    const idx = currentCueIndex >= 0 ? currentCueIndex : 0;
    const currentCue = activeCues[idx];
    // If more than 1.5s into current cue, restart it; otherwise go to previous
    if (currentTime > currentCue.startTime + 1.5 || idx === 0) {
      audio.currentTime = currentCue.startTime;
      setCurrentTime(currentCue.startTime);
    } else {
      const prevCue = activeCues[idx - 1];
      audio.currentTime = prevCue.startTime;
      setCurrentTime(prevCue.startTime);
    }
  }, [hasActiveCues, activeCues, currentCueIndex, currentTime]);

  const goToNext = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !hasNext) return;
    const nextCue = activeCues[currentCueIndex + 1];
    audio.currentTime = nextCue.startTime;
    setCurrentTime(nextCue.startTime);
  }, [hasNext, activeCues, currentCueIndex]);

  // Subtitle mode buttons config
  const subtitleButtons: { mode: SubtitleMode; label: string; available: boolean }[] = [
    { mode: 'off', label: t('manage.subtitleOff'), available: true },
    { mode: 'en', label: t('manage.subtitleEnglish'), available: hasEnglish },
    { mode: 'zh-tw', label: t('manage.subtitleChinese'), available: hasNative },
    { mode: 'both', label: t('manage.subtitleBoth'), available: hasEnglish && hasNative },
  ];

  return (
    <div className="uap">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onError={() => setAudioError(true)}
      />

      {audioError && (
        <div className="uap__error" role="alert">
          {t('manage.audioLoadError', { defaultValue: 'Audio failed to load. Please try again later.' })}
        </div>
      )}

      {/* Subtitle Display */}
      {hasSubtitles && subtitleMode !== 'off' && (
        <div className="uap__subtitles">
          {(subtitleMode === 'en' || subtitleMode === 'both') && (
            <div className="uap__sub-en">{currentEnglish || '\u00A0'}</div>
          )}
          {(subtitleMode === 'zh-tw' || subtitleMode === 'both') && (
            <div className="uap__sub-zh">{currentChinese || '\u00A0'}</div>
          )}
        </div>
      )}

      {/* Controls Row */}
      <div className="uap__controls">
        {/* Prev Sentence */}
        {hasActiveCues && (
          <button className="uap__nav" onClick={goToPrev} disabled={!hasPrev} aria-label="Previous sentence">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="5" width="3" height="14" rx="1" />
              <path d="M20 19V5L9 12z" />
            </svg>
          </button>
        )}

        {/* Play/Pause */}
        <button className="uap__play" onClick={togglePlay} disabled={audioError} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Next Sentence */}
        {hasActiveCues && (
          <button className="uap__nav" onClick={goToNext} disabled={!hasNext} aria-label="Next sentence">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 5v14l11-7z" />
              <rect x="17" y="5" width="3" height="14" rx="1" />
            </svg>
          </button>
        )}

        {/* Time + Progress */}
        <span className="uap__time">{fmt(displayCurrent)}</span>
        <div
          className="uap__progress"
          ref={progressRef}
          onMouseDown={handleProgressMouseDown}
          onTouchStart={handleProgressTouchStart}
        >
          <div className="uap__progress-fill" style={{ width: `${progressRatio * 100}%` }} />
          <div className="uap__progress-thumb" style={{ left: `${progressRatio * 100}%` }} />
        </div>
        <span className="uap__time">{fmt(displayDuration)}</span>

        {/* Subtitle toggles */}
        {hasSubtitles && (
          <div className="uap__subtitle-btns">
            {subtitleButtons.filter(b => b.available).map(b => (
              <button
                key={b.mode}
                className={`uap__sub-btn ${subtitleMode === b.mode ? `active ${b.mode}` : ''}`}
                onClick={() => setSubtitleMode(b.mode)}
              >
                {b.mode === 'off' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 1l22 22M17 17H4a2 2 0 01-2-2V7a2 2 0 012-2h1m4-2h11a2 2 0 012 2v8a2 2 0 01-1 1.73" />
                  </svg>
                ) : (
                  <span>{b.label}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedAudioPlayer;
